/**
 * <HepcoGfxRail> — procedural extrusion of the Hepco GFX guiding rail along
 * a Part's chained module path.
 *
 * Mounted once per Part (inside the Part's transform group), so the rail
 * inherits Part-transform and shares its frame with the modules.
 *
 * Renders TWO meshes per part: the wide flat baseplate and the smaller
 * spear-profile rail sitting on top of it at the baseplate's outer (-Y)
 * edge. Both are extruded along the same module-chain spline.
 *
 * Scaffolding stage:
 *   - Baseplate is a plain rectangle (no T-slots, no mounting recesses)
 *   - Rail spear undercut approximated as a straight chamfer (not arc)
 *   - No inner-cavity carve-out yet
 *   - No fillets at any Strangpress transitions
 */

import React, { useMemo } from 'react';
import {
  CatmullRomCurve3,
  ExtrudeGeometry,
  Vector3,
  type BufferGeometry,
  type Shape,
} from 'three';
import type { BuiltChain, BuiltModule } from '../../geometry/ChainBuilder.js';
import { sampleModulePath } from '../../geometry/pathMath.js';
import {
  createHepcoGfxBaseplateShape,
  createHepcoGfxRailShape,
  HEPCO_GFX_PROFILE,
  type HepcoGfxRailProfileDims,
} from '../../geometry/HepcoGfxRailProfile.js';

const DEG2RAD = Math.PI / 180;
/** Samples per module along its local path used to fit the spline. */
const SAMPLES_PER_MODULE = 32;

interface HepcoGfxRailProps {
  chain: BuiltChain;
  /** Profile dimensions override (testing / future variants). */
  profile?: HepcoGfxRailProfileDims;
  /** Matte aluminium tint for the baseplate. */
  baseplateColor?: string;
  /** Polished steel tint for the V-rail. */
  railColor?: string;
}

/**
 * Material parameters tuned to evoke the right surface feel without
 * needing PBR textures: roughness mostly drives the visual difference,
 * metalness keeps both surfaces reading as metal.
 */
const BASEPLATE_MATERIAL = {
  color: '#A8ADB3',     // anodised-aluminium grey
  metalness: 0.7,
  roughness: 0.65,      // matte
} as const;
const RAIL_MATERIAL = {
  color: '#D4D8DC',     // polished-steel highlight
  metalness: 1.0,
  roughness: 0.12,      // highly reflective
} as const;

/**
 * Sample each module's local path in module-local frame, then transform every
 * sample by the module's startWorldPose to obtain a continuous polyline in
 * Part-local coordinates. Consecutive modules share a join point: the first
 * sample of every subsequent module is dropped to avoid a duplicate.
 */
function samplePartLocalPoints(
  chain: BuiltChain,
  samplesPerModule: number,
): Vector3[] {
  const points: Vector3[] = [];
  for (const m of chain.modules) {
    appendModuleSamples(points, m, samplesPerModule);
  }
  return points;
}

function appendModuleSamples(
  out: Vector3[],
  m: BuiltModule,
  samplesPerModule: number,
): void {
  const yawRad = m.startWorldPose.yawDeg * DEG2RAD;
  const cos = Math.cos(yawRad);
  const sin = Math.sin(yawRad);
  const ox = m.startWorldPose.positionMm[0];
  const oy = m.startWorldPose.positionMm[1];
  const oz = m.startWorldPose.positionMm[2];
  // First module: include i=0. Subsequent modules: skip i=0 (= previous
  // module's last sample) so the polyline has no duplicate vertex at joins.
  const startI = out.length === 0 ? 0 : 1;
  for (let i = startI; i <= samplesPerModule; i++) {
    const s = (i / samplesPerModule) * m.trackLengthMm;
    const sample = sampleModulePath(m.catalog, s);
    const lx = sample.positionMm[0];
    const ly = sample.positionMm[1];
    const lz = sample.positionMm[2];
    const wx = cos * lx - sin * ly + ox;
    const wy = sin * lx + cos * ly + oy;
    const wz = lz + oz;
    out.push(new Vector3(wx, wy, wz));
  }
}

function extrudeShape(
  curve: CatmullRomCurve3,
  shape: Shape,
  steps: number,
): BufferGeometry {
  const geometry = new ExtrudeGeometry(shape, {
    steps,
    bevelEnabled: false,
    extrudePath: curve,
  });
  geometry.computeVertexNormals();
  return geometry;
}

interface RailGeometries {
  baseplate: BufferGeometry;
  rail: BufferGeometry;
}

function buildRailGeometries(
  chain: BuiltChain,
  profile: HepcoGfxRailProfileDims,
): RailGeometries | null {
  const points = samplePartLocalPoints(chain, SAMPLES_PER_MODULE);
  if (points.length < 2) return null;

  const curve = new CatmullRomCurve3(points, /* closed */ false, 'catmullrom');
  // Steps along the spine: 1 per sample point is enough — the curve is
  // already a spline through every sample, so one extrusion step per
  // sample preserves the source curvature without inventing detail.
  const steps = points.length - 1;

  return {
    baseplate: extrudeShape(curve, createHepcoGfxBaseplateShape(profile), steps),
    rail: extrudeShape(curve, createHepcoGfxRailShape(profile), steps),
  };
}

const HepcoGfxRailImpl: React.FC<HepcoGfxRailProps> = ({
  chain,
  profile = HEPCO_GFX_PROFILE,
  baseplateColor = BASEPLATE_MATERIAL.color,
  railColor = RAIL_MATERIAL.color,
}) => {
  const geometries = useMemo(
    () => buildRailGeometries(chain, profile),
    [chain, profile],
  );
  if (!geometries) return null;
  return (
    <>
      <mesh geometry={geometries.baseplate}>
        <meshStandardMaterial
          color={baseplateColor}
          metalness={BASEPLATE_MATERIAL.metalness}
          roughness={BASEPLATE_MATERIAL.roughness}
        />
      </mesh>
      <mesh geometry={geometries.rail}>
        <meshStandardMaterial
          color={railColor}
          metalness={RAIL_MATERIAL.metalness}
          roughness={RAIL_MATERIAL.roughness}
        />
      </mesh>
    </>
  );
};

export const HepcoGfxRail = React.memo(HepcoGfxRailImpl);
HepcoGfxRail.displayName = 'HepcoGfxRail';
